import moment from 'moment'
import JobChange from '@/models/job_change'
import { formatTimeRange } from '@/utils/date-formatting.js'
import { useLocations } from './useLocations'
import { computed, toRef } from 'vue'
import { useGetters } from 'vuex-composition-helpers'
import JobHistory from '@/models/job-history'
import { formatSecondaryFieldName, formatSecondaryFieldsDisplay } from '../utils/formatting'
import { useJobStops } from '@/components/tickets/history/composables/useJobStops'
import { useInventoryTypes } from '@/composables/inventory-types/useInventoryTypes'
import { changeTypes } from '@/components/tickets/history/composables/ChangeTypes'
import { ImportantChange } from '@/components/tickets/history/composables/ImportantChange'
import { CustomFieldRecord, CustomField } from '@/models/types/custom-field'
import { useCustomFieldPathParser } from './useCustomFieldPathParser'
import { formatDateWithYear } from '@/utils/date-formatting'
import _ from 'lodash'
export function useFormatting(props: {
  date: string | number
  changes: JobHistory[]
  jobStart?: object | null
  jobEnd?: object | null
  jobHubId: string
  customFieldDefinitions?: CustomFieldRecord
}) {
  const changesRef = toRef(props, 'changes')
  const customFieldDefinitionsRef = toRef(props, 'customFieldDefinitions')
  const { knownLocations } = useLocations(changesRef, customFieldDefinitionsRef)
  const { knownJobStops } = useJobStops(changesRef)
  const { getBySid: getUserBySid, getPrincipalBySid } = useGetters('users', [
    'getBySid',
    'getPrincipalBySid',
  ])
  const { getById: getActivityById } = useGetters('activities', ['getById'])
  const { getById: getUnitById } = useGetters('unitNumbers', ['getById'])
  const { getById: getCompanyById } = useGetters('companies', ['getById'])
  const { getBusinessUnitById } = useGetters('businessUnits', ['getBusinessUnitById'])
  const { getInventorySubTypeById } = useInventoryTypes()
  const { getCustomFieldLabel, getCustomFieldDefinition } = useCustomFieldPathParser()

  const getActivityName = (id: number) => {
    const activity = getActivityById.value(id)
    return activity ? `${activity.resourceTypeName} - ${activity.taskName}` : 'Unknown'
  }

  const getUnitName = (id: number) => {
    return getUnitById.value(id)?.name ?? 'Unknown resource'
  }

  const getCompanyName = (id: number) => {
    return getCompanyById.value(id)?.name ?? 'Unknown company'
  }

  const jobHub = computed(() => getBusinessUnitById.value(props.jobHubId))
  const jobOrg = computed(() =>
    jobHub.value ? getBusinessUnitById.value(jobHub.value.ownerId) : null,
  )

  const getLocationName = (id: number) => {
    if (id == null) {
      return 'Undecided location'
    }
    return knownLocations.value[id]?.location ?? 'Unknown location'
  }

  const getInventoryItemName = (id: number) => {
    return getInventorySubTypeById(id)?.name ?? 'Unknown inventory item'
  }

  const getInventoryItemDisplayString = (value: {
    pickup: number
    quantity: number
    sub_type_id: number
  }) => {
    return `${getLocationName(value.pickup)}: ${getInventoryDisplayString(value)}`
  }

  const getStopLocation = (stopId: string) => {
    return knownJobStops.value[stopId]?.locationName ?? 'Unknown stop location'
  }

  const getRequestedStopPrefix = (position: number, stopType: string) =>
    `${stopType} #${position + 1}`

  const getInventoryReceiptDisplayString = (value: {
    stop_id: string
    sub_type_id: number
    quantity: number
    location_id?: number | null
  }) => {
    const location =
      value.location_id != null
        ? getLocationName(value.location_id)
        : getStopLocation(value.stop_id)
    return `${location}: ${getInventoryDisplayString(value)}`
  }

  const getInventoryDisplayString = (value: { sub_type_id: number; quantity: number }) => {
    return `(Qty ${value.quantity}) x ${getInventoryItemName(value.sub_type_id)}`
  }

  const formatAssignmentValue = (change: { id: number; type: string } | undefined | null) => {
    if (!change) return null

    if (change.type === 'assigned_to_pool_id') return 'Pool'

    return change.type === 'resource_id' ? getUnitName(change.id) : getCompanyName(change.id)
  }

  const formatPlannedTimeChange = (
    props: Partial<{
      jobStart?: object | null
      jobEnd?: object | null
    }>,
    startTimeChange?: Partial<JobChange>,
    endTimeChange?: Partial<JobChange>,
  ): ImportantChange | null => {
    if (!startTimeChange && !endTimeChange) return null
    const oldStart = startTimeChange?.originalValue
      ? moment.utc(startTimeChange?.originalValue).local()
      : props.jobStart
    const newStart = startTimeChange?.newValue
      ? moment.utc(startTimeChange?.newValue).local()
      : props.jobStart
    const oldEnd = endTimeChange?.originalValue
      ? moment.utc(endTimeChange?.originalValue).local()
      : props.jobEnd
    const newEnd = endTimeChange?.newValue
      ? moment.utc(endTimeChange?.newValue).local()
      : props.jobEnd

    return {
      strikeLeft: false,
      columnName: 'time_range',
      newValue: formatTimeRange(newStart, newEnd),
      originalValue: `Planned: ${formatTimeRange(oldStart, oldEnd)}`,
    }
  }

  const formatActiveTimeChange = (
    activeTimeChange?: Partial<JobChange>,
    completedTimeChange?: Partial<JobChange>,
  ): ImportantChange | null => {
    if (!activeTimeChange && !completedTimeChange) return null
    const oldActiveTime = activeTimeChange?.originalValue
      ? moment.utc(activeTimeChange.originalValue).local()
      : null
    const newActiveTime = activeTimeChange?.newValue
      ? moment.utc(activeTimeChange.newValue).local()
      : null
    const oldCompletedTime = completedTimeChange?.originalValue
      ? moment.utc(completedTimeChange.originalValue).local()
      : null
    const newCompletedTime = completedTimeChange?.newValue
      ? moment.utc(completedTimeChange.newValue).local()
      : null
    return {
      strikeLeft: false,
      columnName: 'time_range',
      newValue: formatTimeRange(newActiveTime, newCompletedTime),
      originalValue: `Actual: ${formatTimeRange(oldActiveTime, oldCompletedTime)}`,
    }
  }

  const getRequestedStopLocationIdChange = (field: JobChange): ImportantChange => {
    const toReturn = {
      strikeLeft: Boolean(field.originalValue),
      columnName: field.columnName,
    }

    return {
      ...toReturn,
      originalValue:
        field.originalValue == null
          ? `Added ${getRequestedStopPrefix(field.newValue.position, field.newValue.stop_type)}`
          : getLocationName(field.originalValue.location_id),
      newValue: field.newValue == null ? null : getLocationName(field.newValue.location_id),
      prefix:
        field.originalValue == null
          ? null
          : getRequestedStopPrefix(field.originalValue.position, field.originalValue.stop_type),
    }
  }

  const getRequestedInventoryChange = (field: JobChange): ImportantChange => {
    const stopPosition = field.originalValue?.stop_position ?? field.newValue?.stop_position
    const stopType = field.originalValue?.stop_type ?? field.newValue?.stop_type

    return {
      strikeLeft: Boolean(field.originalValue),
      columnName: field.columnName,
      newValue: field.newValue != null ? getInventoryDisplayString(field.newValue) : null,
      originalValue:
        field.originalValue != null ? getInventoryDisplayString(field.originalValue) : 'Added',
      prefix: getRequestedStopPrefix(stopPosition, stopType),
    }
  }

  const formatImportantField = (field: JobChange, change): ImportantChange | null => {
    switch (field.columnName) {
      case 'destination_location_id':
        return {
          strikeLeft: Boolean(field.originalValue),
          columnName: field.columnName,
          newValue: getLocationName(field.newValue),
          originalValue: getLocationName(field.originalValue),
          prefix: 'DropOff',
        }
      case 'location_id':
        return {
          strikeLeft: Boolean(field.originalValue),
          columnName: field.columnName,
          newValue: field.newValue != null ? getLocationName(field.newValue) : null,
          originalValue:
            field.originalValue != null ? getLocationName(field.originalValue) : 'Location',
        }
      case 'activity_type_id':
        return {
          strikeLeft: Boolean(field.originalValue),
          columnName: field.columnName,
          newValue: getActivityName(field.newValue),
          originalValue: getActivityName(field.originalValue),
        }
      case 'assigned_to': {
        const newValue = formatAssignmentValue(field.newValue)
        const originalValue = formatAssignmentValue(field.originalValue)

        if (newValue === originalValue) return null

        return {
          strikeLeft: Boolean(field.originalValue),
          columnName: field.columnName,
          newValue,
          originalValue: originalValue ?? 'Assigned to',
        }
      }
      case 'inventory_item_id': {
        return {
          strikeLeft: Boolean(field.originalValue),
          columnName: field.columnName,
          newValue: field.newValue != null ? getInventoryItemDisplayString(field.newValue) : null,
          originalValue:
            field.originalValue != null
              ? getInventoryItemDisplayString(field.originalValue)
              : 'Added',
        }
      }
      case 'comment': {
        return {
          columnName: field.columnName,
          newValue: field.newValue,
          strikeLeft: false,
          originalValue: field.originalValue,
        }
      }
      case 'location_enter': {
        return {
          columnName: 'Location entered',
          newValue: getLocationName(field.newValue),
          strikeLeft: false,
          originalValue: getUnitName(field.originalValue),
        }
      }
      case 'location_exit': {
        return {
          columnName: 'Location exited',
          newValue: getLocationName(field.newValue),
          strikeLeft: false,
          originalValue: getUnitName(field.originalValue),
        }
      }
      case 'rejection_reason': {
        if (change.type === changeTypes.REJECTED_APPROVAL) {
          return {
            columnName: 'Reason',
            newValue: field.originalValue,
            strikeLeft: false,
            originalValue: field.newValue,
          }
        }
        return null
      }
      case 'inventory_receipt_id': {
        return {
          strikeLeft: Boolean(field.originalValue),
          columnName: field.columnName,
          newValue:
            field.newValue != null ? getInventoryReceiptDisplayString(field.newValue) : null,
          originalValue:
            field.originalValue != null
              ? getInventoryReceiptDisplayString(field.originalValue)
              : 'Added',
        }
      }
      case 'requested_inventory':
        return getRequestedInventoryChange(field)
      case 'requested_stop_location_id':
        return getRequestedStopLocationIdChange(field)
      default:
        // Check if this is a custom_data.* field
        if (field.columnName.startsWith('custom_data.')) {
          return formatCustomDataField(field, props.customFieldDefinitions)
        }
        return null
    }
  }

  const formatLookupValue = (value: unknown, lookupEntity?: string): string => {
    if (value === null || value === undefined) {
      return ''
    }

    const valueStr = String(value)

    switch (lookupEntity) {
      case 'user':
        return getUserBySid.value(valueStr)?.name ?? 'Unknown user'
      case 'company':
        return getCompanyById.value(valueStr)?.name ?? 'Unknown company'
      case 'business_unit':
        return getBusinessUnitById.value(valueStr)?.name ?? 'Unknown business unit'
      case 'location':
        return knownLocations.value[Number(valueStr)]?.location ?? 'Unknown location'
      case 'inventory_sub_type': {
        const numValue = typeof value === 'number' ? value : parseInt(valueStr, 10)
        const subType = getInventorySubTypeById(numValue)
        return subType?.name ?? 'Unknown inventory item'
      }
      default:
        return valueStr
    }
  }

  const formatValueWithSchema = (value: unknown, fieldDef?: CustomField): string => {
    if (value === null || value === undefined) {
      return ''
    }
    if (fieldDef?.lookup?.entity) {
      return formatLookupValue(value, fieldDef.lookup.entity)
    }

    if (fieldDef?.format === 'data-url') {
      return '<ignore>'
    }

    // Handle date/datetime
    if (fieldDef?.format === 'date' || fieldDef?.format === 'date-time') {
      try {
        if (fieldDef.format === 'date') {
          return formatDateWithYear(value as string | number)
        } else {
          // date-time format: "HH:mm MMM DD, YYYY" per table
          const momentDate = moment(value)
          return momentDate.format('HH:mm MMM DD, YYYY')
        }
      } catch {
        return String(value)
      }
    }

    // Handle boolean (Yes/No)
    if (fieldDef?.dataType === 'boolean' || typeof value === 'boolean') {
      const boolValue = typeof value === 'boolean' ? value : Boolean(value)
      return boolValue ? 'Yes' : 'No'
    }

    // Handle arrays (list/field group list)
    if (fieldDef?.dataType === 'array' || Array.isArray(value)) {
      const arr = value as unknown[]
      return `${arr.length} item${arr.length !== 1 ? 's' : ''}`
    }

    // Handle numbers
    if (fieldDef?.dataType === 'number' || fieldDef?.dataType === 'integer') {
      return String(value)
    }

    // Handle multi-line text - don't show old value on change (handled in formatCustomDataField)
    if (fieldDef?.format === 'textarea') {
      return String(value)
    }

    // Default: string or unknown
    return String(value)
  }

  const formatArrayChange = (
    originalValue: unknown,
    newValue: unknown,
  ): {
    originalValue: string
    newValue: string
    isArraySummary?: boolean
  } | null => {
    const originalArr = Array.isArray(originalValue) ? originalValue : []
    const newArr = Array.isArray(newValue) ? newValue : []

    if (originalArr.length === 0 && newArr.length === 0) {
      return null
    }

    // Set value: "Field name: 10 items added"
    if (originalArr.length === 0 && newArr.length > 0) {
      return {
        originalValue: '',
        newValue: `${newArr.length} item${newArr.length !== 1 ? 's' : ''} added`,
        isArraySummary: true,
      }
    }

    // Clear value: "Field name: 10 items → blank"
    if (originalArr.length > 0 && newArr.length === 0) {
      return {
        originalValue: `${originalArr.length} item${originalArr.length !== 1 ? 's' : ''}`,
        newValue: 'blank',
      }
    }

    // Change value: calculate additions or removals based on length difference only.
    // With length-based comparison alone, we can know the net change in count,
    // but not how many items were both added and removed.
    const diff = newArr.length - originalArr.length
    const added = diff > 0 ? diff : 0
    const removed = diff < 0 ? -diff : 0

    if (added > 0) {
      return {
        originalValue: `${originalArr.length} item${originalArr.length !== 1 ? 's' : ''}`,
        newValue: `${added} item${added !== 1 ? 's' : ''} added`,
      }
    }

    if (removed > 0) {
      return {
        originalValue: `${originalArr.length} item${originalArr.length !== 1 ? 's' : ''}`,
        newValue: `${removed} item${removed !== 1 ? 's' : ''} removed`,
      }
    }

    // Same length - might be content change, show as count
    if (originalArr.length === newArr.length && originalArr.length > 0) {
      return {
        originalValue: `${originalArr.length} item${originalArr.length !== 1 ? 's' : ''}`,
        newValue: `${newArr.length} item${newArr.length !== 1 ? 's' : ''}`,
      }
    }

    return null
  }

  const formatCustomDataField = (
    field: JobChange,
    customFieldDefinitions?: CustomFieldRecord,
  ): ImportantChange | null => {
    const label = getCustomFieldLabel(field.columnName, customFieldDefinitions)
    if (label === null) {
      return null
    }
    const fieldDef = getCustomFieldDefinition(field.columnName, customFieldDefinitions)

    // Ignore file picker fields
    if (fieldDef?.format === 'data-url') {
      return null
    }

    const hasOriginal = field.originalValue !== null && field.originalValue !== undefined
    const hasNew = field.newValue !== null && field.newValue !== undefined

    // Handle arrays with add/remove counts
    if (
      fieldDef?.dataType === 'array' ||
      Array.isArray(field.originalValue) ||
      Array.isArray(field.newValue)
    ) {
      const arrayChange = formatArrayChange(field.originalValue, field.newValue)
      if (!arrayChange) {
        return null
      }
      return {
        strikeLeft: hasOriginal,
        columnName: field.columnName,
        originalValue: arrayChange.originalValue,
        newValue: arrayChange.newValue,
        prefix: label,
        isArraySummary: arrayChange.isArraySummary ?? false,
      }
    }

    // Handle object values (when newValue is a plain object)
    // This happens when a new object is added (e.g., custom_data.container[1] with {quantity: 1, type: "Basket"})
    // We need to expand it or format it as a summary
    const isNewValueObject =
      hasNew &&
      !Array.isArray(field.newValue) &&
      typeof field.newValue === 'object' &&
      field.newValue.constructor === Object
    const isOriginalValueObject =
      hasOriginal &&
      !Array.isArray(field.originalValue) &&
      typeof field.originalValue === 'object' &&
      field.originalValue.constructor === Object

    // If we have an object value and no fieldDef (or fieldDef is for an array),
    // we can't format it properly - return null to skip it
    // The expansion should happen in useFormattedChanges before this is called
    if ((isNewValueObject || isOriginalValueObject) && !fieldDef) {
      // This shouldn't happen if expansion is working correctly, but handle gracefully
      return null
    }

    let originalValue = hasOriginal ? formatValueWithSchema(field.originalValue, fieldDef) : ''
    const newValue = hasNew ? formatValueWithSchema(field.newValue, fieldDef) : ''

    // Handle multi-line text - don't show old value on change
    if (fieldDef?.format === 'textarea' && hasOriginal && hasNew) {
      originalValue = '' // Don't show old value per table spec
    }

    // Don't show if values are the same
    if (originalValue === newValue && hasOriginal && hasNew) {
      return null
    }

    // Determine display format based on Set/Change/Clear
    let displayOriginal = originalValue || 'blank'
    let displayNew = newValue || 'blank'

    // For "Set value" case (no original), show as "Field name → value"
    if (!hasOriginal && hasNew) {
      displayOriginal = ''
      displayNew = newValue
    }

    return {
      strikeLeft: hasOriginal,
      columnName: field.columnName,
      originalValue: displayOriginal,
      newValue: displayNew,
      prefix: label,
    }
  }

  const formatType = (type: string, modifiedByUserSid: string, fieldsChanged: JobChange[]) => {
    if (type === changeTypes.APPROVED) {
      const userOrgId = getPrincipalBySid.value(modifiedByUserSid)?.organizationId
      if (userOrgId && jobOrg.value?.ownerId === userOrgId) {
        return `Approved on behalf of ${jobOrg.value.name}`
      } else {
        return 'Approved'
      }
    }

    if (type === changeTypes.ASSIGNED_TO_POOL) {
      return 'Assigned to Pool'
    }
    if (type === changeTypes.AVAILABILITY_ACCEPTED) {
      return 'Claimed'
    }
    if (type === changeTypes.AVAILABILITY_DECLINED) {
      return 'Declined'
    }
    if (type === changeTypes.REJECTED_APPROVAL) {
      return 'Approval rejected'
    }
    if (type === changeTypes.ACTUAL_STOP_DELETED) {
      return 'Stop Confirmations Removed'
    }
    if (type === 'location_enter') {
      return 'Location Entered'
    }
    if (type === 'location_exit') {
      return 'Location Exited'
    }
    if (type === changeTypes.SUBMITTED_FOR_APPROVAL) {
      return 'Submitted for approval'
    }
    if (type === 'approval_request_cancelled') {
      return 'Approval request cancelled'
    }
    if (type === changeTypes.SIGNATURE_DELETED) {
      return 'Signature deleted'
    }
    if (type === changeTypes.SIGNED) {
      const name = fieldsChanged.find((c) => c.columnName === 'name')?.newValue
      const modifiedByUserName = getPrincipalBySid.value(modifiedByUserSid)?.name

      if (name !== modifiedByUserName) {
        return `Signature collected from ${name}`
      }
    }
    if (type === changeTypes.REOPEN) {
      return 'Reopened'
    }
    if (type === 'trigger_threshold_resolved') {
      return 'Trigger threshold resolved'
    }
    if (type === 'item_received_at_location') {
      return 'Item received at location'
    }
    if (type === changeTypes.ACTUAL_STOPS) {
      return 'Job stop'
    }
    if (type === changeTypes.SKIPPED_STOPS) {
      return 'Stop skipped'
    }
    if (type === changeTypes.REQUESTED_STOPS) {
      return 'Requested stops'
    }
    if (type === changeTypes.INVENTORY_RECEIPTS) {
      return 'Inventory receipt'
    }

    // Capitalize the first letter
    return type.charAt(0).toUpperCase() + type.slice(1)
  }

  return {
    formatPlannedTimeChange,
    formatActiveTimeChange,
    formatImportantField,
    formatSecondaryFieldName,
    formatSecondaryFieldsDisplay,
    formatType,
  }
}
